import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { ordersApi } from '../services/api';

/**
 * EditOrderPage - Redirects to NewOrderPage with existing order data
 *
 * This component loads the order data and stores it in sessionStorage,
 * then redirects to NewOrderPage which will detect and load this data
 * for editing.
 */
export const EditOrderPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const intent = searchParams.get('action');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadOrderAndRedirect = async () => {
      if (!id) {
        setError('No order ID provided');
        setLoading(false);
        return;
      }

      try {
        // Fetch the order and its items
        const [order, orderItems] = await Promise.all([
          ordersApi.get(id),
          ordersApi.getItems(id).catch((err) => {
            console.error('[EditOrder] Failed to fetch order items:', err);
            return [];
          }),
        ]);

        console.log('[EditOrder] Order loaded:', order);
        console.log('[EditOrder] Order items loaded:', orderItems);
        console.log('[EditOrder] Order items count:', orderItems?.length || 0);

        // Check if this is a parts order - redirect to parts page instead
        const orderType = (order as any).orderType || (order as any).order_type || 'screen';
        console.log('[EditOrder] Order type:', orderType);

        if (orderType === 'parts') {
          // Store parts order data and redirect to PartsPage
          const partsData = {
            orderId: id,
            orderNumber: order.orderNumber || (order as any).order_number,
            order: order,
            items: orderItems,
          };
          console.log('[EditOrder] Saving parts order data to sessionStorage:', partsData);
          sessionStorage.setItem('editPartsOrderData', JSON.stringify(partsData));
          navigate('/parts', { state: { editMode: true, orderId: id }, replace: true });
          return;
        }

        // Check if order can be edited - allow draft, submitted (pre-payment), pending_payment, needs_correction
        if (!['draft', 'submitted', 'pending_payment', 'needs_correction'].includes(order.status)) {
          setError('This order can no longer be edited. Orders in production or already shipped cannot be modified.');
          setLoading(false);
          return;
        }

        // Parse order_data if present
        let orderData: any = null;
        const rawOrderData = (order as any).order_data || (order as any).orderData;
        if (rawOrderData) {
          try {
            orderData = typeof rawOrderData === 'string' ? JSON.parse(rawOrderData) : rawOrderData;
          } catch (e) {
            console.warn('Could not parse order_data:', e);
          }
        }

        // Convert order_items to unit format for the form
        // The API returns camelCase fields from mapItemRow, so check both formats
        // Also get savedFormData units to merge (they preserve original form enum values)
        const savedUnits = orderData?.formData?.units || orderData?.units || [];

        // Reverse maps: DB enum → form enum
        const installTypeReverseMap: Record<string, string> = {
          'face_of_header': 'wall',
          'under_header': 'wall',
          'ceiling_mount': 'ceiling',
          'recessed': 'wall',
        };
        const trackMountReverseMap: Record<string, string> = {
          'jamb': 'recessed',
        };

        const units = (orderItems || []).map((item: any, index: number) => {
          console.log(`[EditOrder] Mapping item ${index}:`, item);

          // Helper to get value with fallbacks (handles null, undefined, and '' properly)
          const getValue = (camel: any, snake: any, defaultVal: any) => {
            if (camel !== undefined && camel !== null && camel !== '') return camel;
            if (snake !== undefined && snake !== null && snake !== '') return snake;
            return defaultVal;
          };

          // Helper for measurements - convert to string, but 0 means "not entered"
          const getMeasurement = (camel: any, snake: any) => {
            const val = getValue(camel, snake, '');
            // Treat 0 or '0' as empty (not measured)
            if (val === 0 || val === '0' || val === null || val === undefined || val === '') return '';
            return String(val);
          };

          // Get the matching saved form unit (has original form enum values)
          const savedUnit = savedUnits[index] || {};

          const unit: any = {
            id: item.id || savedUnit.id || Math.random().toString(36).substr(2, 9),
            // Measurements - convert numbers to strings, treat 0 as empty
            widthTop: getMeasurement(item.widthTop, item.width_top),
            widthMiddle: getMeasurement(item.widthMiddle, item.width_middle),
            widthBottom: getMeasurement(item.widthBottom, item.width_bottom),
            heightLeft: getMeasurement(item.heightLeft, item.height_left),
            heightCenter: getMeasurement(item.heightCenter, item.height_center),
            heightRight: getMeasurement(item.heightRight, item.height_right),
            // Screen location (per-unit)
            screenLocation: savedUnit.screenLocation || '',
            // Motor and installation options
            motorSide: getValue(item.motorSide, item.motor_side, 'right'),
            uChannel: (() => {
              // DB stores one_side/two_sides, form uses right_side/left_side/both_sides
              const raw = getValue(item.uChannel, item.u_channel, 'none');
              const map: Record<string, string> = { 'one_side': 'right_side', 'two_sides': 'both_sides' };
              return map[raw] || raw;
            })(),
            biasedCut: item.biasedCut || item.biased_cut || false,
            biasAmount: String(getValue(item.biasedCutInches, item.biased_cut_inches, '')),
            biasSide: getValue(item.biasedCutSide, item.biased_cut_side, 'left'),
            // Fabric options
            fabricType: getValue(item.fabricType, item.fabric_type, 'omega_pro_3'),
            fabricColor: getValue(item.fabricColor, item.fabric_color, 'Black'),
            // Installation type — prefer savedUnit (original form enum), else reverse-map from DB
            installationType: savedUnit.installationType || (() => {
              const raw = getValue(item.installationType, item.installation_type, 'wall');
              return installTypeReverseMap[raw] || raw;
            })(),
            recessed: item.recessed ?? false,
            // Track mount — prefer savedUnit (preserves detail like inside_face_left), else reverse-map
            trackMount: savedUnit.trackMount || (() => {
              const raw = getValue(item.trackMount, item.track_mount, 'face');
              return trackMountReverseMap[raw] || raw;
            })(),
            // System type
            systemType: savedUnit.systemType || getValue(item.systemType, item.system_type, 'box'),
            // System color
            systemColor: savedUnit.systemColor || getValue(item.systemColor, item.system_color, 'black'),
            ralNumber: savedUnit.ralNumber || getValue(item.ralNumber, item.ral_number, ''),
            // Brush size
            brushSize: savedUnit.brushSize || getValue(item.brushSize, item.brush_size, '1inch'),
            // Screw covers
            screwCovers: savedUnit.screwCovers ?? item.screwCovers ?? item.screw_covers ?? false,
            screwCoverQty: savedUnit.screwCoverQty ?? item.screwCoverQty ?? item.screw_cover_qty ?? 0,
            // U-channel deduction
            applyUChannelDeduction: item.applyUChannelDeduction || item.apply_u_channel_deduction || false,
            // Box width adjustment
            boxWidthAdjustmentType: savedUnit.boxWidthAdjustmentType || (() => {
              const adj = item.boxWidthAdjustment ?? item.box_width_adjustment;
              if (adj == null || adj === 0) return 'same';
              return adj < 0 ? 'smaller' : 'larger';
            })(),
            boxWidthAdjustmentValue: savedUnit.boxWidthAdjustmentValue || (() => {
              const adj = item.boxWidthAdjustment ?? item.box_width_adjustment;
              if (adj == null || adj === 0) return '';
              return Math.abs(parseFloat(adj)).toString();
            })(),
            applyBoxDeduction: savedUnit.applyBoxDeduction ?? item.applyBoxDeduction ?? item.apply_box_deduction ?? true,
            // Expert mode
            expertMode: item.expertMode || item.expert_mode || false,
            // Window panel — restore from formData snapshot first (richest source),
            // then fall back to the persisted JSONB (order_items.window_data) so the
            // designer rebuilds correctly even if the formData blob is missing the
            // unit-level windowConfig.
            includeWindowPanel: item.windowPanel || item.window_panel || false,
            windowConfig: savedUnit.windowConfig || item.windowData || item.window_data || null,
          };

          console.log(`[EditOrder] Mapped unit ${index}:`, unit);
          return unit;
        });

        console.log('[EditOrder] Total units mapped:', units.length);
        console.log('[EditOrder] Units:', units);

        // Get the formData from order_data, or reconstruct it from order fields
        const savedFormData = orderData?.formData || orderData?.order || {};

        // Store order data for NewOrderPage to load
        const finalUnits = units.length > 0 ? units : (savedFormData.units || []);
        console.log('[EditOrder] Final units to be stored:', finalUnits);
        console.log('[EditOrder] Units count:', finalUnits.length);

        // Extract freight data from order
        const freightData = {
          method: ((order as any).shippingMethod || (order as any).shipping_method) === 'will_call' ? 'will_call' : 'shipping',
          freightviewShipmentId: (order as any).freightviewShipmentId || (order as any).freightview_shipment_id,
          selectedQuoteId: (order as any).selectedQuoteId || (order as any).selected_quote_id,
          selectedCarrierName: (order as any).selectedCarrierName || (order as any).selected_carrier_name,
          selectedCarrierCode: (order as any).selectedCarrierCode || (order as any).selected_carrier_code,
          freightQuotes: (order as any).freightQuotes || (order as any).freight_quotes,
          freightTotalAmount: (order as any).freightTotalAmount || (order as any).freight_total_amount,
        };
        console.log('[EditOrder] Freight data extracted:', freightData);

        // Detect expert mode from any order item
        const hasExpertMode = units.some((u: any) => u.expertMode);

        // Extract customer info from order's customer_lead fields OR savedFormData
        const customerLeadName = (order as any).customerLeadName || (order as any).customer_lead_name || '';
        const customerLeadEmail = (order as any).customerLeadEmail || (order as any).customer_lead_email || '';
        const customerLeadPhone = (order as any).customerLeadPhone || (order as any).customer_lead_phone || '';
        const customerLeadAddress = (order as any).customerLeadAddress || (order as any).customer_lead_address || '';

        // Parse concatenated address ("123 Main St, City, ST 12345") as fallback
        let parsedAddress = '', parsedCity = '', parsedState = '', parsedZip = '';
        if (customerLeadAddress && !savedFormData.customerAddress) {
          const parts = customerLeadAddress.split(',').map((p: string) => p.trim());
          if (parts.length >= 3) {
            parsedAddress = parts[0];
            parsedCity = parts[1];
            const stateZip = parts[2].split(' ');
            parsedState = stateZip[0] || '';
            parsedZip = stateZip[1] || '';
          } else if (parts.length === 2) {
            parsedAddress = parts[0];
            parsedCity = parts[1];
          }
        }

        // Detect u-channel from items if not in savedFormData
        const firstUChannel = units.length > 0 ? units[0].uChannel : 'none';
        const allSameUChannel = units.every((u: any) => u.uChannel === firstUChannel);

        const editData = {
          orderId: id,
          orderNumber: order.orderNumber || (order as any).order_number,
          order: order,
          orderData: orderData,
          freightData: freightData,
          expertMode: hasExpertMode,
          formData: {
            ...savedFormData,
            // Ensure units from order_items are used if available
            units: finalUnits,
            // Pull other fields from order if not in savedFormData
            sidemark: savedFormData.sidemark || order.sidemark || '',
            poNumber: savedFormData.poNumber || order.poNumber || (order as any).po_number || '',
            systemColor: savedFormData.systemColor || (order as any).systemColor || (order as any).system_color || 'black',
            shippingMethod: savedFormData.shippingMethod || (() => {
              const raw = (order as any).shippingMethod || (order as any).shipping_method || 'ship';
              // DB stores 'will_call', form uses 'willcall'
              return raw === 'will_call' ? 'willcall' : raw;
            })(),
            // Keep shippingAddressSource in sync with shippingMethod. Older
            // orders saved before formData snapshots have no source field;
            // if the order column says 'will_call', the dropdown must say
            // 'willcall' too, otherwise the dealer-auth effect's default
            // ('dealer') would visually contradict the persisted method.
            shippingAddressSource: (() => {
              const fromSaved = savedFormData.shippingAddressSource;
              if (fromSaved) return fromSaved;
              const rawMethod = (order as any).shippingMethod || (order as any).shipping_method;
              return rawMethod === 'will_call' ? 'willcall' : undefined;
            })(),
            // Also store global fabric settings if available
            globalFabricType: savedFormData.globalFabricType || orderData?.globalFabricType || 'omega_pro_3',
            globalFabricColor: savedFormData.globalFabricColor || orderData?.globalFabricColor || 'Black',
            sameFabricForAll: savedFormData.sameFabricForAll ?? orderData?.sameFabricForAll ?? true,
            // Installation settings (prefer savedFormData which has original form enums)
            sameInstallationForAll: savedFormData.sameInstallationForAll ?? true,
            sameSystemType: savedFormData.sameSystemType ?? true,
            sameInstallType: savedFormData.sameInstallType ?? true,
            sameTrackMount: savedFormData.sameTrackMount ?? true,
            sameBrushSize: savedFormData.sameBrushSize ?? true,
            globalSystemType: savedFormData.globalSystemType || 'box',
            globalInstallationType: savedFormData.globalInstallationType || (() => {
              const raw = (order as any).installationType || (order as any).installation_type || 'wall';
              return installTypeReverseMap[raw] || raw;
            })(),
            globalRecessed: savedFormData.globalRecessed ?? (order as any).recessed ?? false,
            globalTrackMount: savedFormData.globalTrackMount || (() => {
              const raw = (order as any).trackMount || (order as any).track_mount || 'face';
              return trackMountReverseMap[raw] || raw;
            })(),
            // U-channel settings (restore from savedFormData or detect from items)
            sameUchannel: savedFormData.sameUchannel ?? allSameUChannel,
            globalUChannel: savedFormData.globalUChannel || firstUChannel || 'none',
            // Screw cover settings
            sameScrewCovers: savedFormData.sameScrewCovers ?? true,
            globalScrewCovers: savedFormData.globalScrewCovers ?? false,
            // Motor and controller settings
            motorType: savedFormData.motorType || orderData?.motorType || 'Alpha',
            remoteType: savedFormData.remoteType || orderData?.remoteType || 'alpha_pioneer_1ch',
            remoteQty: savedFormData.remoteQty || orderData?.remoteQty || 1,
            wallSwitchType: savedFormData.wallSwitchType || orderData?.wallSwitchType || 'none',
            wallSwitchQty: savedFormData.wallSwitchQty || orderData?.wallSwitchQty || 0,
            // Customer info (from savedFormData, or from order's customer_lead fields)
            customerName: savedFormData.customerName || customerLeadName || '',
            customerEmail: savedFormData.customerEmail || customerLeadEmail || '',
            customerPhone: savedFormData.customerPhone || customerLeadPhone || '',
            customerAddress: savedFormData.customerAddress || parsedAddress || '',
            customerCity: savedFormData.customerCity || parsedCity || '',
            customerState: savedFormData.customerState || parsedState || '',
            customerZip: savedFormData.customerZip || parsedZip || '',
          },
          invoice: orderData?.invoice || {},
          items: orderItems,
          isEdit: true,
        };

        console.log('[EditOrder] Storing edit data to sessionStorage:', editData);
        sessionStorage.setItem('editOrderData', JSON.stringify(editData));

        // Redirect to NewOrderPage. Forward ?action=pay so the form scrolls
        // to the Authorization section (dealers re-opening a draft to pay
        // shouldn't have to scroll through the whole config).
        navigate(`/orders/new${intent === 'pay' ? '?action=pay' : ''}`, {
          state: { editMode: true, orderId: id, action: intent || undefined },
          replace: true
        });
      } catch (err: any) {
        console.error('Failed to load order for editing:', err);
        setError(err.message || 'Failed to load order');
        setLoading(false);
      }
    };

    loadOrderAndRedirect();
  }, [id, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-keder-yellow mx-auto"></div>
          <p className="mt-4 text-gray-400">Loading order for editing...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-keder-dark border border-red-500 rounded-lg p-6 text-center">
          <div className="text-red-400 text-5xl mb-4">!</div>
          <h2 className="text-xl font-bold text-white mb-2">Cannot Edit Order</h2>
          <p className="text-gray-400 mb-6">{error}</p>
          <button
            onClick={() => navigate('/orders')}
            className="px-6 py-2 bg-keder-yellow text-black font-semibold rounded-lg hover:bg-yellow-400 transition-colors"
          >
            Back to Orders
          </button>
        </div>
      </div>
    );
  }

  return null;
};

export default EditOrderPage;
